import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/api/client";
import { startAlarm, stopAlarm } from "@/utils/sound";
import { BellRing, Clock } from "lucide-react";

/**
 * AlarmOverlay — mounted globally in AppLayout. Polls /api/alarms/due every
 * 10 s and, the moment the backend returns a due alarm the user hasn't
 * dismissed, renders a fullscreen amber modal + starts a continuous alarm
 * buzzer (see utils/sound.js#startAlarm). The user MUST hit "Stop alarm" (or
 * "Snooze 5 min") — the buzzer keeps ringing otherwise.
 *
 * The first pass on load defers audio start until any user interaction to
 * comply with the browser autoplay policy — clicking anywhere else on the
 * page + a due alarm existing will kick off the sound.
 */
export default function AlarmOverlay() {
  const [active, setActive] = useState(null);   // the alarm currently ringing
  const [snoozing, setSnoozing] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const audioUnlocked = useRef(false);
  const pending = useRef(null);   // alarm queued while audio still locked

  const startForAlarm = useCallback((alarm) => {
    setActive(alarm);
    if (audioUnlocked.current) startAlarm();
    else pending.current = alarm;
  }, []);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/alarms/due");
      if (!Array.isArray(data) || data.length === 0) return;
      const first = data[0];
      // Only interrupt the user if we're not already ringing for the same one.
      setActive((cur) => (cur && cur.id === first.id ? cur : first));
      if (!audioUnlocked.current) pending.current = first;
      else startAlarm();
    } catch (_e) { /* silent — network blip, try again next tick */ }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 10_000);
    // Also react instantly when the service worker forwards a push event —
    // otherwise the user waits up to 10 s for the next poll tick.
    const swListener = (event) => {
      if (event.data?.type === "LIM_ALARM_PUSH") poll();
    };
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", swListener);
    }
    return () => {
      clearInterval(t);
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", swListener);
      }
    };
  }, [poll]);

  // Unlock the browser's AudioContext on the first user interaction. If an
  // alarm was already queued (arrived before any click) start it now.
  useEffect(() => {
    const unlock = () => {
      audioUnlocked.current = true;
      if (pending.current) {
        startAlarm();
        pending.current = null;
      }
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
    window.addEventListener("click", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("touchstart", unlock);
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, []);

  // Stop the buzzer when the overlay unmounts / user navigates away.
  useEffect(() => () => stopAlarm(), []);

  const handleStop = async () => {
    if (!active) return;
    setDismissing(true);
    try {
      await api.post(`/alarms/${active.id}/dismiss`);
    } catch (_e) { /* still stop the buzzer locally */ }
    stopAlarm();
    setActive(null);
    setDismissing(false);
  };

  const handleSnooze = async (minutes) => {
    if (!active) return;
    setSnoozing(true);
    try {
      await api.post(`/alarms/${active.id}/snooze?minutes=${minutes}`);
    } catch (_e) { /* silent */ }
    stopAlarm();
    setActive(null);
    setSnoozing(false);
  };

  startForAlarm; // eslint keep

  if (!active) return null;

  const fireTime = active.fire_at
    ? new Date(active.fire_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <div
      data-testid="alarm-overlay"
      className="fixed inset-0 z-[9999] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-6"
    >
      <div className="max-w-md w-full rounded-2xl border-2 border-amber-500 bg-surface p-6 shadow-2xl shadow-amber-500/30 animate-pulse-slow">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-12 w-12 rounded-full bg-amber-500/20 border-2 border-amber-400 flex items-center justify-center">
            <BellRing size={24} className="text-amber-300 animate-bounce" />
          </div>
          <div>
            <div className="text-[0.7rem] uppercase tracking-widest text-amber-300 font-mono">Alarm ringing</div>
            <div className="text-2xl font-display text-white leading-tight">{active.title}</div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3 text-xs text-white/70 font-mono">
          <Clock size={12} className="text-amber-300" />
          <span>{fireTime}</span>
          <span className="text-white/30">·</span>
          <span>from {active.created_by_name || "System"}</span>
        </div>

        {active.description && (
          <p className="text-sm text-white/70 mb-4 whitespace-pre-wrap">{active.description}</p>
        )}

        <div className="flex flex-col gap-2">
          <button
            onClick={handleStop}
            disabled={dismissing}
            data-testid="alarm-stop-btn"
            className="w-full py-3 rounded-lg bg-[#E11D2E] hover:bg-[#F03041] text-white font-display text-lg tracking-wide transition disabled:opacity-40"
          >
            {dismissing ? "Stopping…" : "Stop alarm"}
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => handleSnooze(5)}
              disabled={snoozing}
              data-testid="alarm-snooze-5-btn"
              className="flex-1 py-2 rounded-lg border border-white/20 text-white/80 hover:bg-white/10 text-sm transition disabled:opacity-40"
            >
              Snooze 5 min
            </button>
            <button
              onClick={() => handleSnooze(15)}
              disabled={snoozing}
              data-testid="alarm-snooze-15-btn"
              className="flex-1 py-2 rounded-lg border border-white/20 text-white/80 hover:bg-white/10 text-sm transition disabled:opacity-40"
            >
              Snooze 15 min
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
